// A horse loading station — a 2x2 roadside dock where a horse team pulls up: a
// raised timber loading deck with a sloped ramp and goods ready to ship at the
// back, and a pair of feed troughs heaped with hay at the road-facing front
// (+z) where the horses stand and eat. Same Age-of-MCP classic language as
// ramp.ts (stone footings + timber deck, foundation->complete build stages),
// reusing the library's trough() for the fodder. The horses themselves are
// supplied by the consumer: each feed slot publishes a station anchor.
import * as THREE from "three";
import { defineModel, foundationSlab, scaffold, trough, barrel, type Part } from "../../lib";

const STONE = "#6a6a6a"; // footings
const DECK = "#8a5f34"; // timber deck
const POST = "#5e3f24"; // posts / frame
const RAIL = "#6b4a26"; // guard rails
const PLATE = "#3a3a3a"; // ramp tread plate
const HAY = "#d9b24a"; // fodder
const HAY_DARK = "#c9a23f";

const BASE = 0.14;
const DECK_Y = 0.44; // top of the raised deck
const DECK_W = 1.6; // deck span in x
const DECK_D = 0.7; // deck depth in z
const DECK_CZ = -0.5; // deck centre z (set back toward -z)

// The raised loading deck slab + its four supporting posts.
function deck(): Part[] {
  const out: Part[] = [
    { geometry: new THREE.BoxGeometry(DECK_W, 0.1, DECK_D), color: DECK, position: [0, DECK_Y - 0.05, DECK_CZ] },
  ];
  const ox = DECK_W / 2 - 0.1, oz = DECK_D / 2 - 0.1;
  for (const [x, z] of [[-ox, DECK_CZ - oz], [ox, DECK_CZ - oz], [-ox, DECK_CZ + oz], [ox, DECK_CZ + oz]] as [number, number][]) {
    out.push({ geometry: new THREE.BoxGeometry(0.12, DECK_Y - 0.1, 0.12), color: POST, position: [x, (DECK_Y - 0.1) / 2 + BASE, z] });
  }
  return out;
}

// A sloped tread-plate ramp down the deck's road-facing front (+z) to the
// ground, so a wagon can be backed up to the deck.
function rampSlope(): Part {
  const rise = DECK_Y - 0.06, run = 0.5;
  const len = Math.hypot(rise, run), ang = Math.atan2(rise, run);
  const frontZ = DECK_CZ + DECK_D / 2;
  return {
    geometry: new THREE.BoxGeometry(DECK_W - 0.3, 0.06, len),
    color: PLATE,
    position: [0, DECK_Y / 2, frontZ + run / 2],
    rotation: [-(Math.PI / 2 - ang), 0, 0],
  };
}

// Guard rails along the back and sides of the deck (the front is the ramp).
function rails(): Part[] {
  const ry = DECK_Y + 0.16;
  const ox = DECK_W / 2 - 0.05;
  const backZ = DECK_CZ - DECK_D / 2 + 0.05;
  return [
    { geometry: new THREE.BoxGeometry(DECK_W, 0.04, 0.04), color: RAIL, position: [0, ry, backZ] },
    { geometry: new THREE.BoxGeometry(0.04, 0.04, DECK_D), color: RAIL, position: [-ox, ry, DECK_CZ] },
    { geometry: new THREE.BoxGeometry(0.04, 0.04, DECK_D), color: RAIL, position: [ox, ry, DECK_CZ] },
  ];
}

// Goods stacked on the deck, ready to load onto the wagon.
function cargo(): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.28, 0.28, 0.28), color: "#9a7440", position: [-0.42, DECK_Y + 0.14, DECK_CZ - 0.04] },
    { geometry: new THREE.BoxGeometry(0.24, 0.24, 0.24), color: "#8a6638", position: [-0.12, DECK_Y + 0.12, DECK_CZ + 0.06] },
    { geometry: new THREE.BoxGeometry(0.24, 0.24, 0.24), color: "#9a7440", position: [-0.18, DECK_Y + 0.36, DECK_CZ - 0.04] },
    barrel(0.42, DECK_CZ, DECK_Y, 0.28),
  ];
}

// Two feed troughs heaped with hay at the road-facing front, one per horse,
// flanking the ramp. A few loose hay cones spill over each.
function feed(): Part[] {
  const out: Part[] = [];
  for (const x of [-0.5, 0.5]) {
    out.push(...trough(x, 0.72));
    out.push({ geometry: new THREE.ConeGeometry(0.1, 0.18, 7), color: HAY, position: [x - 0.12, BASE + 0.22, 0.72] });
    out.push({ geometry: new THREE.ConeGeometry(0.08, 0.15, 7), color: HAY_DARK, position: [x + 0.12, BASE + 0.2, 0.72], rotation: [0.2, 0, 0.2] });
  }
  // A spare bale dropped between the troughs — twine bands so it reads as hay,
  // not a crate.
  out.push({ geometry: new THREE.BoxGeometry(0.26, 0.2, 0.34), color: HAY, position: [0, BASE + 0.1, 0.82] });
  out.push({ geometry: new THREE.BoxGeometry(0.27, 0.21, 0.03), color: HAY_DARK, position: [0, BASE + 0.1, 0.7] });
  out.push({ geometry: new THREE.BoxGeometry(0.27, 0.03, 0.35), color: HAY_DARK, position: [0, BASE + 0.16, 0.82] });
  return out;
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [foundationSlab(1.94)];
  if (s === 1) return [foundationSlab(1.94), ...scaffold(DECK_W, 0.5)];
  if (s === 2) return [foundationSlab(1.94), ...deck(), ...scaffold(DECK_W, 0.9)];
  return [
    { geometry: new THREE.BoxGeometry(1.94, BASE, 1.94), color: STONE, position: [0, BASE / 2, 0] },
    ...deck(),
    rampSlope(),
    ...rails(),
    ...cargo(),
    ...feed(),
  ];
}

export const horseStation = defineModel({
  id: "horse-station",
  tiles: { w: 2, h: 2 },
  batchId: "standard",
  name: "Horse station",
  tags: ["building", "horse", "loading", "feed", "low-poly", "age-of-mcp", "classic"],
  summary: "A roadside dock where a horse team is loaded and fed.",
  description:
    "A 2x2 loading station for horse-drawn freight: a raised timber deck with a ramp and goods at the back, feed troughs heaped with hay at the road-facing front. Each feed slot publishes a station anchor where a horse stands.",
  author: "claude",
  footprint: 1,
  // Authored here, no counterpart in age-of-mcp yet — out of sync until pushed there.
  synced: false,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
  // Where each horse of the team stands at its trough: just outside the trough
  // on the +z (road) side, turned to face the fodder. A consumer parks a horse
  // model here; the station is the model publishing "stand here to feed".
  stations: [
    { id: "feed-l", position: [-0.5, 0, 1.0], facing: Math.PI, capacity: 1, description: "Where the left horse of the team stands to feed." },
    { id: "feed-r", position: [0.5, 0, 1.0], facing: Math.PI, capacity: 1, description: "Where the right horse of the team stands to feed." },
  ],
});
